// Клиентский хост app.kluchik.io: один билд обслуживает и команду
// (gatsby.kluchik.io), и внешних клиентов (app.kluchik.io). Различие — только
// в поведении фронта; бэкенд общий (гейт авторизации — PR24/PR26).

// Страница /settings (src/pages/settings.js) появилась в PR27: пункт меню виден
// ролям admin/client со слагом `settings`, а клиент без ключа маркетплейса попадает
// на неё сразу после логина (getClientLanding).
export const SETTINGS_PAGE_READY = true;

// Хост клиентского приложения. GATSBY_APP_HOST задаётся в сборке под app-домен
// (например, для превью на другом имени); по умолчанию — боевой app.kluchik.io.
export const APP_HOSTNAME = process.env.GATSBY_APP_HOST || 'app.kluchik.io';

// SSR: при сборке window нет — считаем, что это командный хост.
export const isAppHost = () =>
  typeof window !== 'undefined' && window.location.hostname === APP_HOSTNAME;

// ---- База n8n ячейки (PR51, .plan/cells-1.md допущение 2) ------------------
// Логин всегда идёт в NL; логин другой ячейки получает redirect.n8n_base, и эта база
// кладётся в сессию (`analytics-n8n-base`, рядом с `analytics-token`, чистится
// clearAuthStorage). Все вебхуки строят URL через getN8nBase() В МОМЕНТ ВЫЗОВА —
// SSR и модульный кэш иначе зафиксировали бы NL. Единственное место литерала хоста
// n8n в src/ (scripts/check-n8n-base.mjs).
export const DEFAULT_N8N_BASE = process.env.GATSBY_N8N_BASE || 'https://n8n.kluchik.io';
export const N8N_BASE_KEY = 'analytics-n8n-base';
// Белый список: база из ответа/localStorage становится адресом, куда уходят пароль и токен.
const CELL_BASES = [DEFAULT_N8N_BASE, 'https://n8n.kluchik.io', 'https://n8n.kluchik.io/rf'];
export const isCellBase = (b) => typeof b === 'string' && CELL_BASES.includes(b);

export const getN8nBase = () => {
  if (typeof window === 'undefined') return DEFAULT_N8N_BASE;
  try {
    const b = localStorage.getItem(N8N_BASE_KEY);
    if (isCellBase(b)) return b;
  } catch {
    /* localStorage закрыт — база по умолчанию */
  }
  return DEFAULT_N8N_BASE;
};

// ---- Payload логина, который кладёт PR24 ----------------------------------
// analytics-sellers-meta: [{ user_id, seller_name, is_external, tools, brand_label }]
// analytics-needs-setup:  'true' | 'false' — у фирмы нет активного ключа маркетплейса.
// Ключей может не быть (старый воркфлоу логина, сессия до PR24) → фолбэк.
// Оба ключа стоят в AUTH_STORAGE_KEYS (AuthWrapper.js), их чистит clearAuthStorage().

// Единственный геттер sellers_meta: AuthWrapper.js его реэкспортирует, страницы
// берут фирмы через getFirmOptions/getFirmName/isExternalFirm оттуда же.
// null = данных нет (логин до PR24) — у всех потребителей на этот случай фолбэк.
// Returns array of allowed user_id, or null = все фирмы (unscoped / team).
export const getAllowedSellers = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('analytics-sellers');
    if (!raw || raw === 'null') return null;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) && arr.length ? arr.map(Number) : null;
  } catch {
    return null;
  }
};

export const getSellersMeta = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem('analytics-sellers-meta');
    if (!raw || raw === 'null') return null;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) && arr.length ? arr : null;
  } catch {
    return null;
  }
};

export const getNeedsSetup = () => {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem('analytics-needs-setup') === 'true';
  } catch {
    return false;
  }
};

// Вордмарка клиента вместо нашего лого. До наката миграции 116 (brand_label в
// wb_sellers) метаданных нет — остаётся прежняя карта, поведение не меняется.
const LEGACY_BRANDS = { 5: 'Funny Slippers' };

export const getBrandLabel = (userIds) => {
  if (!Array.isArray(userIds) || userIds.length !== 1) return null;
  const id = Number(userIds[0]);
  const meta = getSellersMeta();
  // `m &&` — битый элемент массива не должен ронять рендер сайдбара
  const row = meta && meta.find((m) => m && Number(m.user_id) === id);
  // Метаданные по фирме есть → они и решают: пустой brand_label = наше лого
  // (миграция 116, «null = наше лого»). Метаданных нет (логин до PR24) →
  // прежняя карта, поведение не меняется.
  return row ? (row.brand_label || null) : (LEGACY_BRANDS[id] || null);
};

// ---- Фирмы логина: списки и признаки вместо литералов --------------------
// Источник — sellers_meta: [{user_id, seller_name, is_external, tools, brand_label}]
// (verify_app_user, миграция 116). null = данных нет (логин до PR24): у каждого
// геттера ниже на этот случай прежний фолбэк, поведение не меняется.
// Матрица списков по страницам зафиксирована в scripts/check-firm-options.mjs.
// Фирма без активного ключа маркетплейса данных не даёт и сегодня не появляется
// ни в одном списке страниц, а признака ключа в sellers_meta нет.
// multi-ok: TODO(PR31) — has_key в verify_app_user, тогда эта строка уходит.
const NO_KEY_FIRMS = [3]; // Пешеход

const firmRow = (id) => {
  const meta = getSellersMeta();
  return (meta && meta.find((m) => m && Number(m.user_id) === Number(id))) || null;
};

// Имя фирмы для заголовков и меток. Фолбэк — прежняя метка (сессия до перелогина).
export const getFirmName = (id) =>
  firmRow(id)?.seller_name || (Number(id) === 5 ? 'Казанцева' : `Фирма ${id}`);

// Внешний клиент: не входит в командные сводки и не видит наших метрик
// (себестоимость Китай, истинный ДРР, доля в марже). Фолбэк — прежний `=== 5`.
export const isExternalFirm = (id) => {
  const row = firmRow(id);
  return row ? !!row.is_external : Number(id) === 5;
};

// Список фирм для селектора страницы: фирмы логина ∩ его права.
//   fallback — прежний литерал страницы [{user_id,label}]: пока sellers_meta в сессии
//              нет (логин до PR24), список берётся из него и поведение не меняется;
//   opts.team — инструмент команды: внешние фирмы (is_external) не показываем;
//   opts.tool — слаг инструмента: фирма видна, если её tools = null или содержит слаг;
//   opts.ids  — жёсткое подмножество user_id (оно же задаёт порядок) для правила,
//               которого в payload логина нет (кабинеты Ozon) — помечать «multi-ok»;
//   opts.marketplace — 'wb' | 'ozon': фирма видна, если у неё есть активный ключ этого
//               маркетплейса (`marketplaces` из sellers_meta, миграция 121). Поля нет
//               (сессия до перелогина) → фильтр не применяется и работает прежний
//               отбор (`ids` / NO_KEY_FIRMS), то есть поведение ровно как до PR36.
export const getFirmOptions = (fallback, opts = {}) => {
  const meta = getSellersMeta();
  const allowed = getAllowedSellers(); // null = команда, видит все свои фирмы
  let rows = meta
    ? meta
      .filter((m) => m && !NO_KEY_FIRMS.includes(Number(m.user_id)))
      .map((m) => ({
        user_id: Number(m.user_id),
        label: m.seller_name,
        is_external: !!m.is_external,
        tools: m.tools,
        marketplaces: m.marketplaces,
      }))
    : fallback.map((f) => ({ ...f, is_external: Number(f.user_id) === 5, tools: null, marketplaces: null }));
  if (opts.tool) rows = rows.filter((r) => !Array.isArray(r.tools) || r.tools.includes(opts.tool));
  if (opts.marketplace) {
    // Коды в БД в нижнем регистре (`check (marketplace in ('wb','ozon'))`), но опечатка
    // вызова («Ozon») дала бы пустой селектор молча — сравниваем без учёта регистра.
    const mp = String(opts.marketplace).toLowerCase();
    rows = rows.filter((r) => !Array.isArray(r.marketplaces)
      || r.marketplaces.some((x) => String(x).toLowerCase() === mp));
  }
  // ponytail: `team` отсеивает внешних только у логина без scope — у scoped-логина
  // весь список и так его собственный. Внутренний логин с явным sellers=[1,2,4,5]
  // увидел бы внешнюю фирму в командном списке; такого логина нет, заводить —
  // добавить признак «внутренний» в app_users, а не угадывать по составу sellers.
  if (allowed) rows = rows.filter((r) => allowed.includes(r.user_id));
  else if (opts.team) rows = rows.filter((r) => !r.is_external);
  if (opts.ids) rows = opts.ids.map((id) => rows.find((r) => r.user_id === Number(id))).filter(Boolean);
  return rows.map((r) => ({ user_id: r.user_id, label: r.label }));
};
